import React, { useCallback, useMemo, useState } from "react";

import { useIntl } from "react-intl";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import Located from "@mapmycustomers/shared/types/base/Located";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import {
  convertCoordinatesToGeoPoint,
  convertPlaceToAddress,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import CreateCompanyModal from "@app/component/createEditEntity/CreateCompanyModal";
import CreatePersonModal from "@app/component/createEditEntity/CreatePersonModal";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import useMapView from "@app/component/view/utils/useMapView";
import PinPopover from "@app/scene/map/components/PinPopover";
import { convertPlaceToEntity } from "@app/scene/map/utils/convertPlaceToEntity";
import { default as formatCountryNameFromCode } from "@app/util/countries/formatCountryName";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";

interface Props {}

const MapPoints: React.FC<Props> = () => {
  const { map } = useMap();
  const {
    updateViewStateAndFetch,
    viewState: { visibleEntities },
  } = useMapView();

  const intl = useIntl();
  const [address, setAddress] = useState<Located | undefined>();
  const [coordinates, setCoordinates] = useState<GeolocationCoordinates | undefined>();
  const [visible, show, hide] = useBoolean();
  const [loading, showLoading, hideLoading] = useBoolean();
  const [createCompanyVisible, showCreateCompany, hideCreateCompany] = useBoolean();
  const [companyInitialValues, setCompanyInitialValues] = useState<Partial<Company>>();
  const [createPersonVisible, showCreatePerson, hideCreatePerson] = useBoolean();
  const [personInitialValues, setPersonInitialValues] = useState<Partial<Person>>();
  const [placeResult, setPlaceResult] = useState<google.maps.places.PlaceResult | undefined>();

  const previewName = useMemo(() => placeResult?.name ?? "", [placeResult]);

  const placesService = useMemo(
    () => new google.maps.places.PlacesService(document.createElement("div")),
    []
  );

  const formatCountryName = useCallback(
    (countryCode: CountryCode) => {
      return intl ? formatCountryNameFromCode(intl, countryCode) : "";
    },
    [intl]
  );

  useMapEventHandler(map, "click", (e: google.maps.IconMouseEvent | google.maps.MapMouseEvent) => {
    if ("placeId" in e) {
      const coordinatesObject: google.maps.LatLngLiteral | undefined = e.latLng?.toJSON();
      if (coordinatesObject) {
        setCoordinates({
          latitude: coordinatesObject.lat,
          longitude: coordinatesObject.lng,
        } as GeolocationCoordinates);
        show();
        showLoading();
        placesService.getDetails({ placeId: e.placeId as string }, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            setPlaceResult(result);
            setAddress(convertPlaceToAddress(formatCountryName, result) as Located);
            hideLoading();
          }
        });
      }
    }
  });

  const handleCreateCompany = useCallback(() => {
    if (placeResult && coordinates) {
      setCompanyInitialValues({
        ...convertPlaceToEntity(EntityType.COMPANY, placeResult),
        geoPoint: convertCoordinatesToGeoPoint(coordinates),
      });
      showCreateCompany();
      hide();
    }
  }, [coordinates, hide, placeResult, showCreateCompany]);

  const handleCreatePerson = useCallback(() => {
    if (placeResult && coordinates) {
      setPersonInitialValues({
        ...convertPlaceToEntity(EntityType.PERSON, placeResult),
        geoPoint: convertCoordinatesToGeoPoint(coordinates),
      });
      showCreatePerson();
      hide();
    }
  }, [coordinates, hide, placeResult, showCreatePerson]);

  const handleHideCompanyModal = useCallback(
    (newCompany?: Company) => {
      hideCreateCompany();
      if (newCompany && !visibleEntities.includes(EntityType.COMPANY)) {
        updateViewStateAndFetch({
          request: { visibleEntities: [...visibleEntities, EntityType.COMPANY] },
        });
      }
    },
    [hideCreateCompany, visibleEntities, updateViewStateAndFetch]
  );

  const handleHidePersonModal = useCallback(
    (newCompany?: Person) => {
      hideCreatePerson();
      if (newCompany && !visibleEntities.includes(EntityType.PERSON)) {
        updateViewStateAndFetch({
          request: { visibleEntities: [...visibleEntities, EntityType.PERSON] },
        });
      }
    },
    [hideCreatePerson, visibleEntities, updateViewStateAndFetch]
  );

  return (
    <>
      <PinPopover
        address={address}
        coordinates={coordinates}
        loading={loading}
        onCancel={hide}
        onCreateCompany={handleCreateCompany}
        onCreatePerson={handleCreatePerson}
        placeResult={placeResult}
        title={previewName}
        visible={visible}
      />
      {createCompanyVisible && (
        <CreateCompanyModal initialValues={companyInitialValues} onHide={handleHideCompanyModal} />
      )}

      {createPersonVisible && (
        <CreatePersonModal initialValues={personInitialValues} onHide={handleHidePersonModal} />
      )}
    </>
  );
};

export default MapPoints;
